import React from "react";
import {
  AnalyticsChartRow,
  CategoryActivityChart,
} from "../CategoryActivityChart";
import type { PeriodType } from "./periodType";

const EMPTY_VALUES: number[] = [];

export type { PeriodType } from "./periodType";

export interface OccupancyMetricsChartProps {
  occupancyData?: number[];
  upsellsData?: number[];
  guestSatisfactionData?: number[];
  categories?: string[];
  periodType?: PeriodType;
  showGuestSatisfaction?: boolean;
  showUpsells?: boolean;
}

export const OccupancyMetricsChart: React.FC<OccupancyMetricsChartProps> = ({
  occupancyData = EMPTY_VALUES,
  upsellsData = EMPTY_VALUES,
  guestSatisfactionData = EMPTY_VALUES,
  categories,
  periodType = "7d",
  showGuestSatisfaction = true,
  showUpsells = true,
}) => {
  const defaultCategories = React.useMemo(() => {
    if (categories) return categories;

    const dataLength = Math.max(
      occupancyData.length,
      upsellsData.length,
      guestSatisfactionData.length,
    );

    const weekShort = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    switch (periodType) {
      case "7d":
        return weekShort.slice(0, dataLength);
      case "14d":
        return Array.from({ length: dataLength }, (_, i) => weekShort[i % 7]);
      case "1m":
        return Array.from({ length: dataLength }, (_, i) => String(i + 1));
      case "1y":
        return [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ].slice(0, dataLength);
      default:
        return [];
    }
  }, [
    categories,
    periodType,
    occupancyData.length,
    upsellsData.length,
    guestSatisfactionData.length,
  ]);

  const chartRows: AnalyticsChartRow[] = defaultCategories.map(
    (category, index) => {
      const gs = guestSatisfactionData[index];
      const hasGuestSat =
        typeof gs === "number" && Number.isFinite(gs) && gs >= 0;
      return {
        category,
        occupancy: occupancyData[index] || 0,
        guestSatRaw: showGuestSatisfaction && hasGuestSat ? gs : -1,
        guestSatScaled:
          showGuestSatisfaction && hasGuestSat ? (gs / 5) * 100 : 0,
        upsells: showUpsells ? upsellsData[index] || 0 : 0,
      };
    },
  );

  return (
    <CategoryActivityChart
      data={chartRows}
      showGuestSatisfaction={showGuestSatisfaction}
      showUpsells={showUpsells}
    />
  );
};
